import React, { useState } from 'react';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Popover from '@mui/material/Popover';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { IngramDiscount } from '../hooks/ingramProgramsSearch';
import DiscountForm from './DiscountForm';
import { useUpdateIngramDiscount } from '../hooks/ingramProgramsMutations';
import { useSearchParams } from 'react-router-dom';
import { CenterLoader } from '@im/ui-lib/CenterLoader';
import { useTranslate } from '@im/react-localization';
import useSnackBar from 'src/hooks/useSnackBar';

const ActionPoper = (props: Readonly<{ row: IngramDiscount }>) => {
  const { row } = props;

  const translate = useTranslate();
  const { showSnackBar } = useSnackBar();
  const [searchParams] = useSearchParams();
  const countryCode = searchParams.get('countryCode');
  const companyCode = searchParams.get('companyCode');

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const updateDiscount = useUpdateIngramDiscount({
    applicability: row.applicability,
    company_code: companyCode ?? '',
    country_code: countryCode ?? '',
    discount_amount: String(row.discountAmount),
    discount_calc_type: row.discountCalcType,
    discount_type: row.discountType,
    effective_date: row.effectiveDate,
    expiration_date: row.expirationDate,
    name: row.name,
    program_id: row.programId,
    status_code: row.statusCode === 'D' ? 'A' : 'D',
  });

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDiscountActivateDeactivate = () => {
    updateDiscount
      .mutateAsync()
      .then(() => {
        handleClose();
        showSnackBar({
          autoHide: 2000,
          message: translate('IngramProgram.EditDiscountSuccess'),
          severity: 'success',
        });
      })
      .catch(() => {
        handleClose();
        showSnackBar({
          autoHide: 2000,
          message: translate('IngramProgram.EditDiscountError'),
          severity: 'error',
        });
      });
  };

  const isOpen = Boolean(anchorEl);

  return (
    <Box>
      {updateDiscount.isLoading && <CenterLoader overlay={true} />}
      <IconButton onClick={handleOpen}>
        <MoreVertIcon />
      </IconButton>
      <Popover
        anchorEl={anchorEl}
        anchorOrigin={{
          horizontal: 'left',
          vertical: 'bottom',
        }}
        onClose={handleClose}
        open={isOpen}
        transformOrigin={{
          horizontal: 'left',
          vertical: 'top',
        }}
      >
        <List
          sx={{
            '.MuiListItemIcon-root': { minWidth: '40px' },
            backgroundColor: 'aliceblue',
          }}
        >
          <DiscountForm
            buttonName={translate('Common.Edit')}
            discountDetails={row}
            ingramProgramId={row.programId}
            popOverClose={handleClose}
          />
          {row.statusCode === 'D' ? (
            <ListItemButton onClick={() => handleDiscountActivateDeactivate()}>
              <ListItemIcon>
                <CheckCircleIcon color="success" />
              </ListItemIcon>
              <ListItemText primary={translate('Common.Activate')} />
            </ListItemButton>
          ) : (
            <ListItemButton onClick={() => handleDiscountActivateDeactivate()}>
              <ListItemIcon>
                <CancelIcon color="error" />
              </ListItemIcon>
              <ListItemText primary={translate('Common.Deactivate')} />
            </ListItemButton>
          )}
        </List>
      </Popover>
    </Box>
  );
};

export default ActionPoper;
